import React from 'react';
import { Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import OpenEventDetail from '../../../../../components/link/OpenEventDetail';
import { PromotionSchedule } from '../../../../../lib/api/promotionSchedule';
import { STATUS_LABEL, TRACK_META, TYPE_LABEL } from '../../constants';
import { getDayCount, getTimeRangeLabel, toDateTimePart, toDatePart } from '../../utils';

/** 기간 칸 — 날짜 줄과 보조 표기(일수·시각) 줄 */
const StyledPeriodCell = styled.div`
  .range {
    display: block;
    white-space: nowrap;
  }

  .meta {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
  }
`;

const STATUS_COLOR: Record<string, string> = {
  PLANNED: 'default',
  CONFIRMED: 'blue',
  CANCELED: 'red',
};

/** 표에서 비어 있음을 나타내는 표기 — 빈 칸보다 "확인했고 없다" 로 읽힌다 */
const EMPTY = '-';

/**
 * 프로모션 리스트 컬럼 (AMZUWEB-682).
 *
 * 타임라인과 **같은 표기 상수**(`TRACK_META`·`STATUS_LABEL`·`TYPE_LABEL`)를 쓴다 —
 * 같은 항목이 화면마다 다른 색·다른 말로 보이면 같은 것인지 사람이 확신하지 못한다.
 *
 * 정렬은 서버가 한다(`sorter: true`). 비교 함수를 주면 antd 가 현재 페이지 안에서만
 * 다시 정렬해 페이지 경계가 어긋난다 — 자세한 내용은 `ListTable` 주석 참고.
 */
export const buildScheduleListColumns = (): ColumnsType<PromotionSchedule> => [
  {
    title: '번호',
    dataIndex: 'id',
    key: 'id',
    width: 80,
    sorter: true,
  },
  {
    title: '트랙',
    dataIndex: 'track',
    key: 'track',
    // 가장 긴 라벨이 "일반 기획전 (상품 연합)" 이다 — 태그가 접히지 않을 만큼만 준다
    width: 180,
    /**
     * antd `Tag` 는 기본 `margin-inline-end: 8px` 를 갖는다 — 원래 태그를 여러 개 나열하려고
     * 붙은 값이라, 한 칸에 하나만 둘 때는 **가운데 정렬이 왼쪽으로 밀려 보인다.**
     * 오른쪽만 비어 보이는 원인이라 0 으로 지운다 (상태 칸도 같다).
     */
    render: (track: PromotionSchedule['track']) => (
      <Tag color={TRACK_META[track]?.color} style={{ marginInlineEnd: 0 }}>
        {TRACK_META[track]?.label ?? track}
      </Tag>
    ),
  },
  {
    title: '항목명',
    dataIndex: 'title',
    key: 'title',
    ellipsis: true,
    sorter: true,
    // 길이가 들쭉날쭉한 값은 가운데 정렬하면 줄마다 시작점이 달라져 훑기 어렵다
    className: 'is-text-left',
  },
  {
    title: '기간',
    /**
     * ⚠️ `key` 를 `'period'` 로 두면 안 된다 — antd 가 정렬 이벤트에 이 값을 `columnKey` 로 실어 보내고,
     * 그대로 서버에 나가면 **서버가 모르는 컬럼**으로 정렬하게 된다.
     * 화면 표기는 기간이지만 정렬 기준은 시작일이므로 키는 `startDate` 다.
     */
    key: 'startDate',
    dataIndex: 'startDate',
    width: 200,
    sorter: true,
    /**
     * **일부러 두 줄로 나눈다.**
     *
     * 한 줄로 두면 칸 폭에 따라 `(11` / `일)` 처럼 아무 데서나 접힌다 — 폭을 넓혀 막아도
     * 시간 지정 건은 뒤에 `· 10:00~22:00` 이 더 붙어 다시 접힌다.
     * 날짜(찾을 때 보는 값)를 윗줄에 두고 일수·시각은 아랫줄 보조 표기로 내린다.
     */
    render: (_: unknown, row: PromotionSchedule) => {
      const timeRange = getTimeRangeLabel(row.startDate, row.endDate);
      const dayCount = getDayCount(row.startDate, row.endDate);

      return (
        <StyledPeriodCell>
          <span className="range">
            {toDatePart(row.startDate)} ~ {toDatePart(row.endDate)}
          </span>
          <span className="meta">
            {dayCount}일{timeRange ? ` · ${timeRange}` : ''}
          </span>
        </StyledPeriodCell>
      );
    },
  },
  {
    title: '상태',
    dataIndex: 'status',
    key: 'status',
    width: 90,
    render: (status: PromotionSchedule['status']) => <Tag color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Tag>,
  },
  {
    title: '유형',
    dataIndex: 'type',
    key: 'type',
    width: 120,
    render: (type: PromotionSchedule['type']) => (type ? TYPE_LABEL[type] : EMPTY),
  },
  {
    title: '담당자',
    dataIndex: 'adminName',
    key: 'adminName',
    width: 110,
    render: (adminName: string | null) => adminName ?? EMPTY,
  },
  {
    title: '기획전',
    key: 'event',
    width: 180,
    ellipsis: true,
    className: 'is-text-left',
    /**
     * `eventId` 는 남았는데 `event` 가 없으면 **기획전이 지워진 것**이다.
     * 그냥 비워 두면 "연결을 안 한 건"과 구분이 안 되므로 id 라도 보여 준다.
     */
    render: (_: unknown, row: PromotionSchedule) => {
      if (row.event) {
        /**
         * 행 클릭은 프로모션 상세로 가므로, 기획전 링크는 **전파를 막아야** 한다 —
         * 안 막으면 기획전을 누른 사람이 프로모션 상세로 끌려간다.
         */
        return (
          <span onClick={(event) => event.stopPropagation()} role="presentation">
            <OpenEventDetail text={row.event.mainTitle} eventId={row.event.id} />
          </span>
        );
      }

      return row.eventId === null ? (
        EMPTY
      ) : (
        // 지워진 기획전은 링크를 걸지 않는다 — 없는 페이지로 보내면 원인을 못 찾는다
        <Tooltip title="연결된 기획전을 불러오지 못했습니다 (삭제되었을 수 있습니다).">
          <span>#{row.eventId}</span>
        </Tooltip>
      );
    },
  },
  {
    title: '쿠폰',
    key: 'coupons',
    width: 90,
    /**
     * 쿠폰은 여러 개라 리스트에서는 **건수만** 보여주고 이름은 툴팁에 둔다 —
     * 한 칸에 다 펼치면 행 높이가 쿠폰 수에 따라 들쭉날쭉해진다.
     * 개별 쿠폰으로 가는 링크는 상세 화면이 맡는다.
     */
    render: (_: unknown, row: PromotionSchedule) =>
      row.coupons.length === 0 ? (
        EMPTY
      ) : (
        <Tooltip title={row.coupons.map((coupon) => `#${coupon.id} ${coupon.name}`).join(', ')}>
          <span>{row.coupons.length}건</span>
        </Tooltip>
      ),
  },
  {
    /** 등록·수정 일시는 초까지 보여준다 — 같은 날 여러 번 고치는 운영이라 날짜만으로는 순서를 못 읽는다 */
    title: '생성일',
    dataIndex: 'createDate',
    key: 'createDate',
    width: 170,
    sorter: true,
    render: (createDate: string) => toDateTimePart(createDate),
  },
  {
    title: '수정일',
    dataIndex: 'updateDate',
    key: 'updateDate',
    width: 170,
    sorter: true,
    render: (updateDate: string) => toDateTimePart(updateDate),
  },
];
